import { apiClient } from "@/lib/api";
import type {
  Slip,
  SlipType,
  SlipWriteBody,
  StorageAllocation,
  StorageSummary,
} from "@/types/storage";

// --- Reads ---

export const fetchSlips = async (type?: SlipType): Promise<Slip[]> => {
  return apiClient<Slip[]>("/storage/slips", {
    params: type ? { type } : undefined,
  });
};

export const fetchSlipById = async (id: string): Promise<Slip> => {
  return apiClient<Slip>(`/storage/slips/${id}`);
};

export const fetchSlipForBoat = async (boatId: string): Promise<Slip> => {
  return apiClient<Slip>("/storage/slips/by-boat", { params: { boat_id: boatId } });
};

export const fetchStorageSummary = async (): Promise<StorageSummary> => {
  return apiClient<StorageSummary>("/storage/slips/summary");
};

export const fetchAllocations = async (activeOnly = false): Promise<StorageAllocation[]> => {
  return apiClient<StorageAllocation[]>("/storage/allocations", {
    params: { active: activeOnly ? "true" : "false" },
  });
};

// --- Slip writes ---

export const createSlip = async (body: SlipWriteBody): Promise<Slip> =>
  apiClient<Slip>("/storage/slips", { method: "POST", body });

export const updateSlip = async (id: string, body: SlipWriteBody): Promise<Slip> =>
  apiClient<Slip>(`/storage/slips/${id}`, { method: "PUT", body });

export const deleteSlip = async (id: string): Promise<void> =>
  apiClient<void>(`/storage/slips/${id}`, { method: "DELETE" });

// --- Assignment ---

// monthlyRate (dollars/month, optional) is stored on the allocation and drives
// the finance ledger's recurring storage-rental entries.
export const assignBoatToSlip = async (
  slipId: string,
  boatId: string,
  monthlyRate?: number,
  customerId?: string,
): Promise<Slip> =>
  apiClient<Slip>(`/storage/slips/${slipId}/assign`, {
    method: "POST",
    body: {
      boat_id: boatId,
      monthly_rate: monthlyRate,
      customer_id: customerId,
    },
  });

// Sets (or clears) the customer billed for an allocation — the payer behind
// the AR storage invoices (finance module). null clears it.
export const updateAllocationPayer = async (
  allocationId: string,
  customerId: string | null,
): Promise<StorageAllocation> =>
  apiClient<StorageAllocation>(`/storage/allocations/${allocationId}/payer`, {
    method: "PUT",
    body: { customer_id: customerId },
  });

export const vacateSlip = async (slipId: string): Promise<Slip> =>
  apiClient<Slip>(`/storage/slips/${slipId}/vacate`, { method: "POST" });

// Updates the monthly rate on an existing allocation (null clears it — the
// allocation stops billing). Already-recorded ledger entries are not rewritten.
export const updateAllocationRate = async (
  allocationId: string,
  monthlyRate: number | null,
): Promise<StorageAllocation> =>
  apiClient<StorageAllocation>(`/storage/allocations/${allocationId}`, {
    method: "PUT",
    body: { monthly_rate: monthlyRate },
  });
